import React from 'react';

import DefaultAppIcon from '@atlaskit/icon/core/app';
import { LoomIcon } from '@atlaskit/logo/loom/icon';
import { token } from '@atlaskit/tokens';

import { GoogleDocsIcon } from '@atlassian/logo-third-party/google-docs';
import { GoogleDriveIcon } from '@atlassian/logo-third-party/google-drive';
import { GoogleSheetsIcon } from '@atlassian/logo-third-party/google-sheets';
import { GoogleSlidesIcon } from '@atlassian/logo-third-party/google-slides';
import { MicrosoftOneDriveIcon } from '@atlassian/logo-third-party/microsoft-onedrive';
import { MicrosoftSharePointIcon } from '@atlassian/logo-third-party/microsoft-sharepoint';
import { MicrosoftTeamsIcon } from '@atlassian/logo-third-party/microsoft-teams';

import {
	EXTERNAL_PRODUCT_CONNECTION,
	type ExternalProductConnection,
} from './__types__/RemoteObject.ts';

export type LogoSize = 'xxsmall' | 'xsmall' | 'small' | 'medium' | 'large' | 'xlarge';

export type LogoKey =
	| 'google-drive'
	| 'google-docs'
	| 'google-sheets'
	| 'google-slides'
	| 'onedrive'
	| 'microsoft-teams'
	| 'microsoft-sharepoint'
	| 'loom';

const CONNECTION_LOGO_KEY: Record<ExternalProductConnection, LogoKey> = {
	[EXTERNAL_PRODUCT_CONNECTION.GOOGLE_DRIVE]: 'google-drive',
	[EXTERNAL_PRODUCT_CONNECTION.MICROSOFT]: 'onedrive',
	[EXTERNAL_PRODUCT_CONNECTION.LOOM]: 'loom',
};

type ProductLogoProps = {
	logoKey?: LogoKey;
	size?: LogoSize;
	label?: string;
	testId?: string;
};

export const ProductLogo = ({
	logoKey,
	size = 'xsmall',
	label = '',
	testId,
}: ProductLogoProps): React.JSX.Element => {
	switch (logoKey) {
		case 'google-drive':
			return <GoogleDriveIcon label={label} size={size} testId={testId} />;
		case 'google-docs':
			return <GoogleDocsIcon label={label} size={size} testId={testId} />;
		case 'google-sheets':
			return <GoogleSheetsIcon label={label} size={size} testId={testId} />;
		case 'google-slides':
			return <GoogleSlidesIcon label={label} size={size} testId={testId} />;
		case 'onedrive':
			return <MicrosoftOneDriveIcon label={label} size={size} testId={testId} />;
		case 'microsoft-teams':
			return <MicrosoftTeamsIcon label={label} size={size} testId={testId} />;
		case 'microsoft-sharepoint':
			return <MicrosoftSharePointIcon label={label} size={size} testId={testId} />;
		case 'loom':
			return <LoomIcon label={label} size={size} testId={testId} />;
		default:
			return <DefaultAppIcon label={label} color={token('color.icon.subtle')} testId={testId} />;
	}
};

type ConnectionLogoProps = {
	connection: ExternalProductConnection;
	size?: LogoSize;
	label?: string;
	testId?: string;
};

export const ConnectionLogo = ({
	connection,
	size,
	label,
	testId,
}: ConnectionLogoProps): React.JSX.Element => (
	<ProductLogo
		logoKey={CONNECTION_LOGO_KEY[connection]}
		size={size}
		label={label}
		testId={testId}
	/>
);
